import { localizationService, redirect } from "@state";
import { StorageStateKeysEnum } from "@utils/storage";
import { action, computed, observable, runInAction } from "mobx";
import { CountryLookup } from "@services/user/DataConfirmationService";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { getCurrentCulture } from "@utils";
import { UserAuthStore } from "@state/shared";
import PersonalDataService from "@services/account-settings/PersonalDataService";
import { UserStatus } from "@api-types/user/LoginResponseDto";
import AgencyUserApiService from "@api-services/user/AgencyUserApiService";
import { DateTime } from "luxon";
import { UserData } from "./types/TUserData";
import { PlayerPromoSendingMode } from "@api-types/user/AgencyUserDto";
import { LoaderStore } from "@state/stores/common";
import { isEqual } from "lodash";

const loadFailPath = `/${getCurrentCulture()}/app-update`;
// prettier-ignore
const DataConfirmationService = lazy(loadFailPath, ()=>import("@services/user/DataConfirmationService"))

export class UserDataConfirmationStore {
	userAuthStore: UserAuthStore;
	loaderStore: LoaderStore;

	@observable public userDataToBeConfirmed?: UserData | null;
	@observable public isUserConfirmationInitialized: boolean = false;
	@observable public userDataConfirmationCountryLookups:
		| CountryLookup[]
		| null = null;
	@observable shouldShowDataConfirmationEmptyState: boolean = false;
	@observable isUserDataConfirmationPopupActive: boolean = false;

	@computed public get usersCountryName(): string | undefined | null {
		return this.userDataConfirmationCountryLookups?.find(
			(c) => c.id === this.userDataToBeConfirmed?.countryId
		)?.name;
	}

	constructor(userAuthStore: UserAuthStore) {
		this.userAuthStore = userAuthStore;
		this.loaderStore = new LoaderStore();
	}

	public getReturnParam() {
		const params = new URLSearchParams(window.location.search);
		const returnUrl = params.get("return");

		return returnUrl ? returnUrl : null;
	}

	public redirectToReturnUrl(returnParam: string | null) {
		if (returnParam) {
			redirect(`/${getCurrentCulture()}${returnParam}`);
		}
	}

	@action.bound
	public redirectToDataConfirmation() {
		this.hidePopup();

		const currentUrl = window.location.pathname;

		redirect(
			`/${getCurrentCulture()}/user-data-confirmation?return=${currentUrl.slice(3)}`
		);
	}

	@action.bound
	setShouldShowDataConfirmationEmptyState(
		shouldShowDataConfirmationEmptyState: boolean
	) {
		this.shouldShowDataConfirmationEmptyState =
			shouldShowDataConfirmationEmptyState;
	}

	@action.bound
	showPopup() {
		this.isUserDataConfirmationPopupActive = true;
	}

	@action.bound
	hidePopup() {
		this.isUserDataConfirmationPopupActive = false;
	}

	@action.bound
	public async resolveUserData() {
		if (this.userDataToBeConfirmed != null) {
			return;
		}

		if (this.userAuthStore.user != null) {
			await this.resolveUserConfirmationData();
		} else {
			this.userAuthStore.accountActivationStore.setConfirmationDataState();
		}
	}

	@action.bound
	public async resolveUserConfirmationData() {
		await this.userAuthStore.loginStore.resolveUserDataConfirmationData({
			primary: {
				accountActivation: this.userAuthStore.accountActivation!,
				loginHistory: this.userAuthStore.loginHistory!,
				token: this.userAuthStore.token!,
				user: this.userAuthStore.user!,
				userAccounts: this.userAuthStore.userAccounts!,
			},
			secondary: {
				accountActivation: this.userAuthStore.accountActivation!,
				loginHistory: this.userAuthStore.loginHistory!,
				token: this.userAuthStore.secondaryToken!,
				user: this.userAuthStore.secondaryUser!,
				userAccounts: this.userAuthStore.userAccounts!,
			},
		});
	}

	@action.bound
	public checkIsSaveDisabled(data: Partial<UserData>): boolean {
		if (!this.userDataToBeConfirmed) return false;

		const normalizedOriginal = {
			...this.userDataToBeConfirmed,
			dob: new Date(this.userDataToBeConfirmed.dob).getDate().toString(),
		};

		const isDifferent = !isEqual(normalizedOriginal, data);
		return isDifferent;
	}

	@action.bound
	public async updatePersonalData(data: UserData) {
		try {
			this.loaderStore.suspend();

			const response =
				await PersonalDataService.updatePersonalDataForOnlineUser({
					...this.userDataToBeConfirmed,
					...data,
					dob: DateTime.fromISO(data.dob).toFormat("yyyy-MM-dd"),
					//@ts-expect-error
					receiveNewsletter: this.userDataToBeConfirmed
						?.receiveNewsletter
						? PlayerPromoSendingMode.enabled
						: PlayerPromoSendingMode.disabled,
				});

			if (response != null) {
				await this.setUserDataToBeConfirmed({
					...this.userDataToBeConfirmed!,
					...response,
				});

				const agencyUser = await AgencyUserApiService.getUserInfo();

				if (this.userAuthStore.user) {
					this.userAuthStore.user.recheckDueDate =
						agencyUser.userRecheckDueDate;
				}

				this.userAuthStore.updateUserVerificationStatusToFullyVerified(
					agencyUser.insicStatus == UserStatus.Checked
				);

				this.userAuthStore.rootStore.notificationStore.success(
					localizationService.t(
						"USER.ACCOUNT_SETTINGS.PERSONAL_DATA.PERSONAL_DATA_UPDATE_SUCCESS"
					)
				);
			}
		} catch (error) {
			console.error(error);

			if (error?.errorCode == 400266) {
				return;
			} else {
				this.userAuthStore.rootStore.notificationStore.error(
					localizationService.t(
						"USER.POPUPS.DATA_CONFIRMATION.GENERIC_ERROR"
					)
				);
				return;
			}
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	public async setUserDataToBeConfirmed(data: UserData): Promise<void> {
		this.userDataToBeConfirmed = data;
		if (this.userDataToBeConfirmed != null) {
			this.userAuthStore.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.USER_DATA_TO_BE_CONFIRMED,
				JSON.stringify(this.userDataToBeConfirmed)
			);
		} else {
			this.userAuthStore.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.USER_DATA_TO_BE_CONFIRMED
			);
		}
	}

	@action.bound
	public async onInitializeUserDataConfirmation(): Promise<void> {
		const result = await (
			await DataConfirmationService
		).default.FetchCountryLookups();

		if (result?.result != null) {
			this.userDataConfirmationCountryLookups = result.result.item;
		} else if (result?.error != null) {
			console.error(result.error);
		}

		runInAction(() => (this.isUserConfirmationInitialized = true));
	}

	@action.bound
	public async onUserConfirmData(): Promise<void> {
		if (this.userDataToBeConfirmed == null) {
			return;
		}

		try {
			const response = await (
				await DataConfirmationService
			).default.AcceptUserDataConfirmation();

			if (response) {
				runInAction(() => {
					this.userDataToBeConfirmed = null;
					this.userAuthStore.loginStore.setUserDataConfirmationState(
						false
					);
				});
			}

			const returnParam = this.getReturnParam();

			if (returnParam) {
				this.setShouldShowDataConfirmationEmptyState(false);
				this.redirectToReturnUrl(returnParam);
			}
		} catch (error) {
			console.error(error);

			this.userAuthStore.rootStore.notificationStore.error(
				localizationService.t(
					"USER.POPUPS.DATA_CONFIRMATION.GENERIC_ERROR"
				)
			);
		}
	}

	@action.bound
	public async onCancelUserDataConfirmation(): Promise<void> {
		const returnParam = this.getReturnParam();

		if (returnParam) {
			this.redirectToReturnUrl(returnParam);

			this.onDisposeUserDataConfirmation();

			return;
		}

		this.onDisposeUserDataConfirmation();
		this.userAuthStore.logoutUser();
	}

	@action.bound
	onDisposeUserDataConfirmation(): void {
		this.userDataConfirmationCountryLookups = null;
		this.userDataToBeConfirmed = null;
		this.isUserConfirmationInitialized = false;
	}
}
